import React, { useEffect, useMemo } from "react";
import { useAggrMetricsAcrossDimensions } from "../../../WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { WIDGET_ELEM_processRawConfigFn } from "../../../WidgetBarChart/WIDGET_ELEM_processRawConfigFn";
import { WidgetErrorState, WidgetLoadingState } from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../../../ReportLandingContext";
import { filterByHiddenDatapoints, filterChildrenByHiddenDatapoints } from "../../../utils/filterHiddenDatapoints";
import { readVisibilityStateFromProps, resolveHidden } from "../../../utils/visibilityState";
import { makeArrayFromLength } from "oolib";
import { HeatMapGrid } from "@/components/dataviz/HeatMapGrid";
import { compileExpression } from "./expression";
import { MAX_TERMS, TERM_KEYS, DEFAULTS } from "./settings";

export { CompareHeatmapConfigForm as ConfigForm } from "./CompareHeatmapConfigForm";

/**
 * Comparison Heat Map — a cross-tab whose cells are ARITHMETIC over several
 * scopes, not a single count. Pick the two axes as usual, then define terms
 * (A, B, …), each its own filter set, and write what to do with them:
 * "A - B", "(A - B) / B * 100".
 *
 * One query per term, each the same two-dimension cross-tab under that term's
 * filters, stitched cell by cell here. That is the cheap version on purpose:
 * a third dimension on ONE query would do it in a single pass whenever every
 * term is a value of the same field, but it could not express a term that is
 * an arbitrary filter set, which is the point of the experiment. Terms are
 * capped at MAX_TERMS because each one costs a query.
 *
 * A cell missing from a term's results counts as 0 there — the pair genuinely
 * has no documents under that filter — so the grid is the union of every
 * term's rows and columns.
 *
 * Drawn on the shared HeatMapGrid in `plusMinusMode`: a difference can be
 * negative, and that grid's usual scale floors at zero, so the mode diverges
 * it from zero and signs the numbers. The cells are handed over in the same
 * row/children shape an aggregation returns, so nothing about the grid is
 * special-cased here.
 */

// Fixed hook slots: hooks cannot be called in a loop, so every slot is always
// called and the unused ones are switched off.
const useTermQueries = ({ terms, dimensions, contentType, baseFilters, id, enabled }) =>
  TERM_KEYS.map((key, i) => {
    const term = terms[i];
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useAggrMetricsAcrossDimensions({
      vizCardId: `${id}-term-${key}`,
      contentType,
      dimensions,
      metrics: [{ type: "count", name: "count" }],
      activeFilters: [...baseFilters, ...(term?.filters || [])],
      queryOptions: { enabled: enabled && !!term },
    });
  });

/** rows → { [rowValue__colValue]: count }, plus the axis values in order. */
const readCells = (data) => {
  const counts = {};
  const rows = [];
  const columns = [];
  const seenRow = new Set();
  const seenCol = new Set();
  for (const row of data || []) {
    const rowValue = String(row.value);
    if (!seenRow.has(rowValue)) {
      seenRow.add(rowValue);
      rows.push({ value: rowValue, display: row.display ?? rowValue });
    }
    for (const child of row.children || []) {
      const colValue = String(child.value);
      if (!seenCol.has(colValue)) {
        seenCol.add(colValue);
        columns.push({ value: colValue, display: child.display ?? colValue });
      }
      counts[`${rowValue}__${colValue}`] = child.count || 0;
    }
  }
  return { counts, rows, columns };
};

/** The axis values of every term, first term's order first. */
const mergeAxis = (lists) => {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const entry of list) {
      if (seen.has(entry.value)) continue;
      seen.add(entry.value);
      out.push(entry);
    }
  }
  return out;
};

export const Component = ({
  id,
  widgetIdx,
  contentType,
  dimensions = [],
  terms = [],
  expression = DEFAULTS.expression,
  valueMode = DEFAULTS.valueMode,
  dimension1Label,
  dimension2Label,
  activeFilters = [],
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  // Everything else this widget receives from config — the per-dimension
  // show/hide state the heat map's data section writes. Must stay last: a rest
  // element can only be the final binding.
  ...visibilityProps
}) => {
  const { updateWidgetDataStore } = useReportLandingContext() || {};
  const baseFilters = useMemo(
    () => [...activeFilters, ...presetActiveFilters, ...widgetFilters],
    [activeFilters, presetActiveFilters, widgetFilters]
  );
  const ready = dimensions.length === 2 && terms.length > 0;
  const queries = useTermQueries({ terms, dimensions, contentType, baseFilters, id, enabled: ready });
  const live = queries.slice(0, terms.length);

  const status = !ready
    ? "success"
    : live.some((q) => q.status === "error")
    ? "error"
    : live.some((q) => q.status === "loading")
    ? "loading"
    : "success";
  const error = live.find((q) => q.status === "error")?.error;

  const termKeys = useMemo(() => terms.map((_, i) => TERM_KEYS[i]), [terms]);
  const compiled = useMemo(() => compileExpression(expression, termKeys), [expression, termKeys]);

  const isPercent = valueMode === "percent";
  // A percent change needs something to be a percent OF — see compileExpression.
  const configError =
    compiled.error ||
    (isPercent && !compiled.baseline
      ? "Percent change needs the expression to end in a subtraction, e.g. B - A."
      : null);

  const grid = useMemo(() => {
    if (status !== "success" || !ready || configError) return null;
    const perTerm = live.map((q) => readCells(q.data));
    const rows = mergeAxis(perTerm.map((t) => t.rows));
    const columns = mergeAxis(perTerm.map((t) => t.columns));
    const cells = {};
    for (const row of rows) {
      for (const col of columns) {
        const cellKey = `${row.value}__${col.value}`;
        const parts = {};
        perTerm.forEach((term, i) => {
          parts[termKeys[i]] = term.counts[cellKey] || 0;
        });
        const change = compiled.evaluate(parts);
        // Percent change: the expression as a percent of what it subtracts.
        // No base (a zero, or nothing subtracted) leaves the cell valueless
        // rather than dividing into Infinity.
        const base = isPercent ? compiled.baseline(parts) : null;
        const value = !isPercent ? change : change === null || !base ? null : (change / base) * 100;
        cells[cellKey] = { value, parts };
      }
    }
    // Back into the row/children shape the grid reads, one child per column
    // so every row has the same columns in the same order.
    const data = rows.map((row) => {
      const children = columns.map((col) => {
        // One decimal: a ratio expression is rarely whole, and the grid
        // prints the number as it is given. A cell with no value (a division
        // by zero) reads 0 — the grid has no empty state for a cell.
        const value = cells[`${row.value}__${col.value}`]?.value;
        return {
          value: col.value,
          display: col.display,
          count: value === null || value === undefined ? 0 : Math.round(value * 10) / 10,
        };
      });
      // A row's size for Top / Bottom N is how much it MOVES, not its signed
      // sum: a row of +40 and −40 is as big as a row of +80.
      const count = children.reduce((acc, child) => acc + Math.abs(child.count), 0);
      return { value: row.value, display: row.display, count, children };
    });
    // Rows in the order a heat map always puts them: by each row's single
    // biggest cell, largest first — what the BE's own `$sort: { count: -1 }`
    // on the flat (row, column) buckets works out to. It cannot come from the
    // BE here because the cells are arithmetic across terms, computed above.
    // Biggest cell by MAGNITUDE, unlike the BE's plain counts: a cell that
    // fell 40 is as big a change as one that rose 40, so a row that dropped
    // hard belongs at the top rather than the bottom.
    const biggestCell = (row) => row.children.reduce((max, child) => Math.max(max, Math.abs(child.count)), 0);
    data.sort((a, b) => biggestCell(b) - biggestCell(a));
    return { data, columns };
  }, [status, ready, compiled, live, termKeys, isPercent, configError]);

  // The heat map's show/hide values list reads the widget's rows from the
  // report's data store — the whole grid, before anything is hidden.
  useEffect(() => {
    if (status === "success" && grid?.data?.length) updateWidgetDataStore?.(id, grid.data);
  }, [status, grid, id, updateWidgetDataStore]);

  useUpdateLoadedWidgetsTracker({ status, widgetIdx, hasData: !!grid?.data?.length, widgetId: id });

  if (status === "loading") return <WidgetLoadingState />;
  if (status === "error") return <WidgetErrorState error={error} />;
  if (configError) return <WidgetErrorState error={{ message: configError }} />;
  if (!grid || !grid.data.length) return null;

  // Manual mode uses the stored selection; dynamic mode recomputes Top/Bottom-N
  // from the current grid — the same reading the heat map widget does.
  const hiddenRows = resolveHidden({ state: readVisibilityStateFromProps(visibilityProps, 1), data: grid.data, dimension: 1 });
  const hiddenColumns = resolveHidden({ state: readVisibilityStateFromProps(visibilityProps, 2), data: grid.data, dimension: 2 });
  const shown = filterChildrenByHiddenDatapoints(filterByHiddenDatapoints(grid.data, hiddenRows), hiddenColumns);
  const longestRow = shown.reduce((max, row) => Math.max(max, row.children?.length || 0), 0);

  const columnSlots = makeArrayFromLength(longestRow);
  return (
    <HeatMapGrid
      data={shown}
      valuePath={columnSlots.map((_, i) => `children.${i}.count`)}
      tooltipLabelsPath={columnSlots.map((_, i) => `children.${i}.display`)}
      labelPath="display"
      plusMinusMode
      showPercent={false}
      style={style}
      xAxisLabel={dimension2Label}
      yAxisLabel={dimension1Label}
    />
  );
};

export const optionDef = {
  display: "Comparison Heat Map",
  icon: "GridNine",
  desc: "A cross-tab whose cells are arithmetic across filter scopes — A minus B, and so on.",
};

export const defaultConfig = { props: { terms: DEFAULTS.terms, expression: DEFAULTS.expression } };

export const someReqPropsUnset = (widgetConfig) => {
  const props = widgetConfig?.props || {};
  return !props.dimension1 || !props.dimension2 || !(props.terms?.length > 1) || !props.expression;
};

// The axes are configured exactly as the heat map's are, so the same shared
// processor turns them into aggrMetrics dimensions. Terms and the expression
// pass through untouched.
export const processRawConfigFn = (widgetConfig) => {
  const processed = WIDGET_ELEM_processRawConfigFn(widgetConfig);
  return {
    ...processed,
    props: {
      ...processed.props,
      dimension1Label: widgetConfig.props?.dimension1?.display,
      dimension2Label: widgetConfig.props?.dimension2?.display,
      // The container appends this to the card's title, so the reader knows
      // which of the two readings the cells are.
      titleSuffix: (widgetConfig.props?.valueMode ?? DEFAULTS.valueMode) === "percent" ? "percent change" : "count change",
    },
  };
};

export { MAX_TERMS };
